import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import {
  ChevronRightIcon,
  ChevronDownIcon,
  UserIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

/**
 * Organization Hierarchy Component
 * Visualizes the organizational structure as an expandable tree
 */
const OrganizationHierarchy = () => {
  const [hierarchyData, setHierarchyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  useEffect(() => {
    loadHierarchy();
  }, []);

  const loadHierarchy = async () => {
    try {
      setLoading(true);

      // Load all data in parallel
      const [orgsResponse, usersResponse] = await Promise.all([
        api.get('/api/admin/organizations'),
        api.get('/api/admin/users?limit=1000'),
      ]);

      const organizations = orgsResponse.data.organizations || [];
      const users = usersResponse.data.users || [];

      // Build hierarchy structure
      const hierarchy = buildHierarchy(organizations, users);
      setHierarchyData(hierarchy);

      // Expand all organizations by default
      const defaultExpanded = new Set();
      organizations.forEach(org => defaultExpanded.add(`org-${org.id}`));
      setExpandedNodes(defaultExpanded);
    } catch (error) {
      console.error('Error loading hierarchy:', error);
      toast.error('Не удалось загрузить иерархию');
    } finally {
      setLoading(false);
    }
  };

  const buildHierarchy = (organizations, users) => {
    // Organize users by organization
    const usersByOrg = {};
    users.forEach(user => {
      if (!user.organization_id) return;
      if (!usersByOrg[user.organization_id]) {
        usersByOrg[user.organization_id] = {
          admins: [],
          directors: [],
          supervisors: [],
          managers: [],
        };
      }
      if (user.role === 'admin') usersByOrg[user.organization_id].admins.push(user);
      else if (user.role === 'director') usersByOrg[user.organization_id].directors.push(user);
      else if (user.role === 'supervisor') usersByOrg[user.organization_id].supervisors.push(user);
      else if (user.role === 'manager') usersByOrg[user.organization_id].managers.push(user);
    });

    // Build hierarchy for each organization
    return organizations.map(org => {
      const orgUsers = usersByOrg[org.id] || { admins: [], directors: [], supervisors: [], managers: [] };

      // Build supervisor -> manager relationships
      const supervisorTree = orgUsers.supervisors.map(supervisor => ({
        ...supervisor,
        managers: orgUsers.managers.filter(m => m.supervisor_id === supervisor.id),
      }));

      // Unassigned managers (no supervisor)
      const unassignedManagers = orgUsers.managers.filter(m => !m.supervisor_id);

      return {
        id: org.id,
        name: org.name,
        isActive: org.is_active,
        directors: orgUsers.directors,
        supervisors: supervisorTree,
        unassignedManagers,
      };
    });
  };

  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const isExpanded = (nodeId) => expandedNodes.has(nodeId);

  const getRoleBadge = (role) => {
    const badges = {
      admin: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Админ' },
      director: { bg: 'bg-indigo-100', text: 'text-indigo-700', label: 'Директор' },
      supervisor: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Супервайзер' },
      manager: { bg: 'bg-green-100', text: 'text-green-700', label: 'Менеджер' },
    };
    return badges[role] || badges.manager;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Загрузка иерархии...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Организационная структура</h1>
        <p className="mt-2 text-sm text-gray-600">
          Визуализация иерархии организаций и пользователей
        </p>
      </div>

      {/* Hierarchy Tree */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {hierarchyData.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <BuildingOfficeIcon className="h-16 w-16 mb-4 text-gray-400" />
            <p className="text-lg font-medium">Нет организаций</p>
            <p className="text-sm mt-2">Создайте организацию для начала работы</p>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {hierarchyData.map((org) => (
              <div key={org.id} className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Organization Header */}
                <div
                  className="bg-gray-50 px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => toggleNode(`org-${org.id}`)}
                >
                  <div className="flex items-center gap-3">
                    {isExpanded(`org-${org.id}`) ? (
                      <ChevronDownIcon className="h-5 w-5 text-gray-600" />
                    ) : (
                      <ChevronRightIcon className="h-5 w-5 text-gray-600" />
                    )}
                    <BuildingOfficeIcon className="h-6 w-6 text-blue-600" />
                    <div>
                      <h3 className="font-semibold text-gray-900">{org.name}</h3>
                      <p className="text-xs text-gray-500">
                        {org.directors.length} директоров, {org.supervisors.length} супервайзеров,{' '}
                        {org.supervisors.reduce((acc, s) => acc + s.managers.length, 0) + org.unassignedManagers.length} менеджеров
                      </p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded ${
                    org.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {org.isActive ? 'Активна' : 'Неактивна'}
                  </span>
                </div>

                {/* Organization Content */}
                {isExpanded(`org-${org.id}`) && (
                  <div className="p-4 space-y-4">
                    {/* Directors */}
                    {org.directors.length > 0 && (
                      <div className="ml-8">
                        <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                          <UserIcon className="h-4 w-4" />
                          Директора
                        </h4>
                        <div className="space-y-2">
                          {org.directors.map((director) => {
                            const badge = getRoleBadge('director');
                            return (
                              <div key={director.id} className="ml-6 flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                                <UserIcon className="h-5 w-5 text-gray-600" />
                                <div className="flex-1">
                                  <p className="font-medium text-gray-900">
                                    {director.first_name} {director.last_name}
                                  </p>
                                  <p className="text-sm text-gray-500">{director.email}</p>
                                </div>
                                <span className={`px-2 py-1 text-xs font-medium rounded ${badge.bg} ${badge.text}`}>
                                  {badge.label}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Supervisors and their Managers */}
                    {org.supervisors.length > 0 && (
                      <div className="ml-8">
                        <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                          <UserGroupIcon className="h-4 w-4" />
                          Супервайзеры и команды
                        </h4>
                        <div className="space-y-3">
                          {org.supervisors.map((supervisor) => {
                            const supBadge = getRoleBadge('supervisor');
                            const nodeId = `sup-${supervisor.id}`;
                            return (
                              <div key={supervisor.id} className="ml-6">
                                {/* Supervisor */}
                                <div
                                  className="flex items-center gap-3 p-2 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors"
                                  onClick={() => toggleNode(nodeId)}
                                >
                                  {supervisor.managers.length > 0 && (
                                    isExpanded(nodeId) ? (
                                      <ChevronDownIcon className="h-4 w-4 text-gray-600" />
                                    ) : (
                                      <ChevronRightIcon className="h-4 w-4 text-gray-600" />
                                    )
                                  )}
                                  <UserIcon className="h-5 w-5 text-gray-600" />
                                  <div className="flex-1">
                                    <p className="font-medium text-gray-900">
                                      {supervisor.first_name} {supervisor.last_name}
                                    </p>
                                    <p className="text-sm text-gray-500">
                                      {supervisor.email} · {supervisor.managers.length} менеджеров
                                    </p>
                                  </div>
                                  <span className={`px-2 py-1 text-xs font-medium rounded ${supBadge.bg} ${supBadge.text}`}>
                                    {supBadge.label}
                                  </span>
                                </div>

                                {/* Managers under this supervisor */}
                                {isExpanded(nodeId) && supervisor.managers.length > 0 && (
                                  <div className="ml-10 mt-2 space-y-2">
                                    {supervisor.managers.map((manager) => {
                                      const manBadge = getRoleBadge('manager');
                                      return (
                                        <div key={manager.id} className="flex items-center gap-3 p-2 bg-green-50 rounded-lg">
                                          <UserIcon className="h-4 w-4 text-gray-600" />
                                          <div className="flex-1">
                                            <p className="text-sm font-medium text-gray-900">
                                              {manager.first_name} {manager.last_name}
                                            </p>
                                            <p className="text-xs text-gray-500">{manager.email}</p>
                                          </div>
                                          <span className={`px-2 py-1 text-xs font-medium rounded ${manBadge.bg} ${manBadge.text}`}>
                                            {manBadge.label}
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Unassigned Managers */}
                    {org.unassignedManagers.length > 0 && (
                      <div className="ml-8">
                        <h4 className="text-sm font-medium text-amber-700 mb-2 flex items-center gap-2">
                          <UserIcon className="h-4 w-4" />
                          Менеджеры без супервайзера
                        </h4>
                        <div className="ml-6 space-y-2">
                          {org.unassignedManagers.map((manager) => {
                            const badge = getRoleBadge('manager');
                            return (
                              <div key={manager.id} className="flex items-center gap-3 p-2 bg-amber-50 rounded-lg">
                                <UserIcon className="h-4 w-4 text-gray-600" />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-gray-900">
                                    {manager.first_name} {manager.last_name}
                                  </p>
                                  <p className="text-xs text-gray-500">{manager.email}</p>
                                </div>
                                <span className={`px-2 py-1 text-xs font-medium rounded ${badge.bg} ${badge.text}`}>
                                  {badge.label}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Empty state */}
                    {org.directors.length === 0 && org.supervisors.length === 0 && org.unassignedManagers.length === 0 && (
                      <div className="ml-8 text-center py-8 text-gray-500">
                        <UserGroupIcon className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                        <p className="text-sm">В этой организации пока нет пользователей</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Легенда:</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries({
            director: 'Директор',
            supervisor: 'Супервайзер',
            manager: 'Менеджер',
            admin: 'Администратор',
          }).map(([role, label]) => {
            const badge = getRoleBadge(role);
            return (
              <div key={role} className="flex items-center gap-2">
                <span className={`px-2 py-1 text-xs font-medium rounded ${badge.bg} ${badge.text}`}>
                  {badge.label}
                </span>
                <span className="text-sm text-gray-600">{label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default OrganizationHierarchy;
